import React from 'react';

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helper?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  errorClassName?: string;
  helperClassName?: string;
  icon?: React.ReactNode;
  required?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  error,
  helper,
  containerClassName = '',
  labelClassName = '',
  inputClassName = '',
  errorClassName = '',
  helperClassName = '',
  icon,
  required = false,
  ...props
}) => {
  const id = props.id || props.name || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;
  
  return (
    <div className={`mb-6 ${containerClassName}`}>
      <label 
        htmlFor={id} 
        className={`
          block text-sm font-medium mb-2
          ${error ? 'text-red-700' : 'text-slate-700'}
          ${labelClassName}
        `}
      >
        {label}
        {required && <span className="text-red-500 ml-1" aria-hidden="true">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <div 
            className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            aria-hidden="true"
          >
            {icon}
          </div>
        )}
        <input
          id={id}
          className={`
            block w-full rounded-md
            ${icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5
            border-2 ${error ? 'border-red-500' : 'border-slate-300'}
            focus:outline-none focus:ring-2 
            ${error ? 'focus:ring-red-500/50' : 'focus:ring-blue-500/50'} 
            focus:border-transparent
            ${error ? 'bg-red-50' : 'bg-white'} 
            transition duration-150 ease-in-out
            placeholder:text-slate-400
            disabled:bg-slate-100 disabled:cursor-not-allowed
            text-slate-900 caret-slate-900
            ${inputClassName}
          `}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={[
            error ? `${id}-error` : null,
            helper ? `${id}-helper` : null,
            required ? `${id}-required` : null
          ].filter(Boolean).join(' ')}
          aria-required={required}
          {...props}
        />
      </div>
      {required && (
        <p 
          id={`${id}-required`} 
          className="sr-only"
        >
          Required field
        </p>
      )}
      {error && (
        <p 
          id={`${id}-error`} 
          className={`mt-2 text-sm font-medium text-red-600 ${errorClassName}`}
          role="alert"
        >
          {error}
        </p>
      )}
      {!error && helper && (
        <p 
          id={`${id}-helper`} 
          className={`mt-2 text-sm text-slate-500 ${helperClassName}`}
        >
          {helper}
        </p>
      )}
    </div>
  );
};

export default InputField;